import { concat } from '@ember/helper';
import { type Registry as Services, service } from '@ember/service';
import Component from '@glimmer/component';
import { PrismTable, styleEntry } from '@mylighthouse/prism-ember';
import type {
  CustomEmberTableColumn,
  CustomEmberTableRow,
} from '@mylighthouse/prism-ember/types/components/prism/table';
import type { Hotelinfo, Ota } from 'frontend-utils/models';
import type { RoomType } from 'frontend-utils/models/roomtype';
import type { Platform } from 'frontend-utils/utils/constants/constant-values';
import { OTA_IDS } from 'frontend-utils/utils/constants/constant-values';
import HotelRatesTablesCellsTdDate from 'lumen/components/hotel/rates/tables/cells/td-date';
import HotelRatesTablesCellsTdHistoryChart from 'lumen/components/hotel/rates/tables/cells/td-history-chart';
import HotelRatesTablesCellsTdMarketDemand from 'lumen/components/hotel/rates/tables/cells/td-market-demand';
import HotelRatesTablesCellsTdMarketOtb from 'lumen/components/hotel/rates/tables/cells/td-market-otb';
import HotelRatesTablesCellsTdOtb from 'lumen/components/hotel/rates/tables/cells/td-otb';
import HotelRatesTablesCellsTdRate from 'lumen/components/hotel/rates/tables/cells/td-rate';
import HotelRatesTablesCellsTdRateCompare from 'lumen/components/hotel/rates/tables/cells/td-rate-compare';
import HotelRatesTablesCellsTdShortTermRentalOtb from 'lumen/components/hotel/rates/tables/cells/td-short-term-rental-otb';
import HotelRatesTablesCellsTdShortTermRentalRate from 'lumen/components/hotel/rates/tables/cells/td-short-term-rental-rate';
import HotelRatesTablesCellsThHotel from 'lumen/components/hotel/rates/tables/cells/th-hotel';
import HotelRatesTablesCellsThMedian from 'lumen/components/hotel/rates/tables/cells/th-median';
import HotelRatesTablesCellsThOtb from 'lumen/components/hotel/rates/tables/cells/th-otb';
import type { Period } from 'lumen/models';
import { isWeekendDate } from 'lumen/utils/charts/chart-utils';
import type {
  CompareType,
  MealTypeValues,
  MembershipType,
  RateParam,
} from 'lumen/utils/constants/general-constants';
import { COMPONENT_DOMAINS } from 'lumen/utils/constants/general-constants';
import type {
  RatesView,
  TableComparisonLayout,
} from 'lumen/utils/constants/rate-constants';
import {
  DAYS_OF_DEMAND_DATA,
  DAYS_OF_DEMAND_DATA_FOR_MI_SUBS,
  RATE_COLUMNS,
} from 'lumen/utils/constants/rate-constants';
import {
  isComparingRates,
  isDateCompare,
} from 'lumen/utils/is-comparing-rates';
import type otbOutOfSync from 'lumen/utils/otb-out-of-sync';

const OTB_SELF_SERVICE_BUTTON_WIDTH = 40;

interface HotelRatesTablesTableRatesSignature {
  Args: {
    compareType: string;
    compareValue: string;
    competitorsForCurrentHotelAndOTA: Hotelinfo[];
    containerHeight: number;
    dayLiveupdateEnabled: boolean;
    hiddenColumnIds: string[];
    isCompareDisabled: boolean;
    isCurrentOrFutureDate: boolean;
    isLoading: boolean;
    isMonthInPast: boolean;
    message: {
      message: string;
      showOverlay: boolean;
    };
    monthOnlyModel: Period[];
    otbOutOfSyncUtil: ReturnType<typeof otbOutOfSync>;
    requestParams: {
      compareType: CompareType;
      compareValue: string;
      compsetId: string;
      los: number;
      maxPersons: number;
      mealPlan: MealTypeValues;
      membershipType: MembershipType;
      otaId: string;
      platform: Platform;
      rateParam: RateParam;
      roomType: RoomType;
      view: RatesView;
    };
    reservedHeight: number;
    selectedOTA: Ota;
    showOTBSelfServiceButton: boolean;
    tableComparisonLayout: TableComparisonLayout;
  };
}

export default class HotelRatesTablesTableRates extends Component<HotelRatesTablesTableRatesSignature> {
  @service declare access: Services['access'];
  @service('hotel-and-market')
  declare hotelAndMarketService: Services['hotel-and-market'];
  @service declare intl: Services['intl'];
  @service declare router: Services['router'];

  get columns(): CustomEmberTableColumn[] {
    const columns: CustomEmberTableColumn[] = [];
    const layoutBasedTextAlign =
      this.args.tableComparisonLayout === 'side-by-side' &&
      !!this.args.compareType
        ? 'left'
        : 'right';

    if (this.showHistory) {
      columns.push({
        name: '',
        valuePath: RATE_COLUMNS.HISTORY,
        tdComponent: HotelRatesTablesCellsTdHistoryChart,
        textAlign: 'left',
        minWidth: 60,
        width: 60,
        maxWidth: 60,
        isFixed: 'left',
        hasMedianRateRow: this.hasMedianRateRow,
      });
    }

    columns.push({
      name: '',
      valuePath: RATE_COLUMNS.DATE,
      tdComponent: HotelRatesTablesCellsTdDate,
      textAlign: 'left',
      minWidth: 144,
      width: 144,
      maxWidth: 144,
      isFixed: 'left',
      hasDayLiveShop: this.args.dayLiveupdateEnabled,
      isOtaSupported: this.args.selectedOTA.hasLiveShop,
      hasMedianRateRow: this.hasMedianRateRow,
      isDateColumn: true,
    });

    if (this.access.hasOTBView) {
      columns.push({
        name: this.intl.t('rates.my_OTB'),
        valuePath: RATE_COLUMNS.MY_OTB,
        thComponent: HotelRatesTablesCellsThOtb,
        tdComponent: HotelRatesTablesCellsTdOtb,
        layout: this.args.tableComparisonLayout,
        textAlign: layoutBasedTextAlign,
        otbOutOfSyncUtil: this.args.otbOutOfSyncUtil,
        isCurrentOrFutureDate: this.args.isCurrentOrFutureDate,
        width: 124,
        minWidth: 124,
        maxWidth: 124,
        isFixed: 'left',
        hasMedianRateRow: this.hasMedianRateRow,
      });
    }

    if (
      this.access.hasFeature('otb.market_benchmark') &&
      !this.args.hiddenColumnIds.includes(RATE_COLUMNS.MARKET_OTB)
    ) {
      columns.push({
        name: this.intl.t('rates.tooltip.market_otb'),
        valuePath: RATE_COLUMNS.MARKET_OTB,
        thComponent: HotelRatesTablesCellsThOtb,
        tdComponent: HotelRatesTablesCellsTdMarketOtb,
        layout: this.args.tableComparisonLayout,
        textAlign: layoutBasedTextAlign,
        otbOutOfSyncUtil: this.args.otbOutOfSyncUtil,
        isCurrentOrFutureDate: this.args.isCurrentOrFutureDate,
        width: 124,
        minWidth: 124,
        maxWidth: 124,
        isFixed: 'left',
        hasMedianRateRow: this.hasMedianRateRow,
        isMarketOTBColumn: true,
      });
    }

    if (
      this.access.hasShortTermRentalAccess &&
      !this.access.hasShortTermRentalOnlyStrTab &&
      !this.args.hiddenColumnIds.includes(RATE_COLUMNS.SHORT_TERM_RENTAL_OTB)
    ) {
      columns.push({
        name: this.intl.t('short_term_rental.otb'),
        valuePath: RATE_COLUMNS.SHORT_TERM_RENTAL_OTB,
        thComponent: HotelRatesTablesCellsThHotel,
        tdComponent: HotelRatesTablesCellsTdShortTermRentalOtb,
        layout: this.args.tableComparisonLayout,
        textAlign: layoutBasedTextAlign,
        width: 124,
        minWidth: 124,
        maxWidth: 124,
        isFixed: 'left',
        hasMedianRateRow: this.hasMedianRateRow,
      });
    }

    const numOfDemandDataDays = this.access.hasMarketInsight
      ? DAYS_OF_DEMAND_DATA_FOR_MI_SUBS
      : DAYS_OF_DEMAND_DATA;
    const isMonthPastRange = this.hotelAndMarketService
      .toLocalTime(
        this.router.currentRoute?.queryParams['month'] as string | undefined,
      )
      .isAfter(
        this.hotelAndMarketService
          .getLocalTime()
          .add(numOfDemandDataDays, 'day'),
        'month',
      );
    const hasDemandData =
      !this.args.isMonthInPast &&
      !isMonthPastRange &&
      this.access.hasFeature('subscription.demand');

    if (
      hasDemandData &&
      !this.args.hiddenColumnIds.includes(RATE_COLUMNS.MARKET_DEMAND)
    ) {
      columns.push({
        name: this.intl.t('demand.market_demand'),
        valuePath: RATE_COLUMNS.MARKET_DEMAND,
        thComponent: HotelRatesTablesCellsThHotel,
        tdComponent: HotelRatesTablesCellsTdMarketDemand,
        layout: this.args.tableComparisonLayout,
        textAlign: layoutBasedTextAlign,
        width: 124,
        minWidth: 124,
        maxWidth: 124,
        isFixed: 'left',
        hasMedianRateRow: this.hasMedianRateRow,
        infoText: this.access.hasMarketInsight
          ? this.intl.t('market_demand.tooltip_di.info').toString()
          : this.intl.t('market_demand.tooltip_ari.info').toString(),
      });
    }

    this.filteredHotelinfos.forEach((hotelinfo) => {
      const isOwnHotelInfo =
        this.hotelAndMarketService.isOwnHotelInfo(hotelinfo);
      if (!this.args.hiddenColumnIds.includes(hotelinfo.id!)) {
        columns.push({
          name: hotelinfo.name,
          valuePath: hotelinfo.id!,
          thComponent: HotelRatesTablesCellsThHotel,
          tdComponent: this.isCompareActive
            ? HotelRatesTablesCellsTdRateCompare
            : HotelRatesTablesCellsTdRate,
          textAlign: layoutBasedTextAlign,
          layout: this.args.tableComparisonLayout,
          minWidth: 148,
          width: 148,
          maxWidth: 180,
          hotelinfo,
          allowedOtaIds: hotelinfo.otas.map((ota: Ota) => ota.id),
          componentDomain: COMPONENT_DOMAINS.RATES, // Used to determine which click action to add to the generic header component
          hasRightBorder: isOwnHotelInfo,
          isFixed: isOwnHotelInfo ? 'left' : undefined,
          hasMedianRateRow: this.hasMedianRateRow,
          hotelinfoId: hotelinfo.id,
          periods: this.args.monthOnlyModel,
          requestParams: this.args.requestParams,
          isRateColumn: true,
          isLoading: this.args.isLoading,
        });
      }
    });

    if (
      this.access.hasShortTermRentalAccess &&
      !this.access.hasShortTermRentalOnlyStrTab &&
      !this.args.hiddenColumnIds.includes(RATE_COLUMNS.SHORT_TERM_RENTAL_RATE)
    ) {
      columns.push({
        name: this.intl.t('short_term_rental.rate'),
        valuePath: RATE_COLUMNS.SHORT_TERM_RENTAL_RATE,
        thComponent: HotelRatesTablesCellsThHotel,
        tdComponent: HotelRatesTablesCellsTdShortTermRentalRate,
        textAlign: layoutBasedTextAlign,
        layout: this.args.tableComparisonLayout,
        minWidth: 124,
        maxWidth: 180,
        hasMedianRateRow: this.hasMedianRateRow,
      });
    }

    // Add an extra column for the median values using the subcolumns array
    if (this.hasMedianRateRow) {
      return columns.map((column) => {
        return {
          ...column,
          thComponent: HotelRatesTablesCellsThHotel,
          hasBottomBorder: true,
          subcolumns: [
            {
              ...column,
              thComponent: HotelRatesTablesCellsThMedian,
              compareType: this.args.compareType,
              textAlign: layoutBasedTextAlign,
            },
          ],
        };
      });
    }

    return columns;
  }

  get filteredHotelinfos(): Hotelinfo[] {
    const availableHotelinfoIds =
      this.args.competitorsForCurrentHotelAndOTA.map(
        (competitor) => competitor.id,
      );

    return this.hotelAndMarketService.ownHotelAndCompetitors.filter(
      (hotelinfo) =>
        this.hotelAndMarketService.isOwnHotelInfo(hotelinfo) ||
        availableHotelinfoIds.includes(hotelinfo.id),
    );
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get hasMedianRateRow() {
    return this.args.compareType && !isDateCompare(this.args.compareType);
  }

  get isCompareActive(): boolean {
    return (
      !this.args.isCompareDisabled &&
      isComparingRates(this.args.compareType, this.args.compareValue)
    );
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get overlayMessage() {
    if (this.args.message.showOverlay) {
      return this.args.message.message;
    }

    return undefined;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get rows() {
    const rows: CustomEmberTableRow[] = [];
    this.args.monthOnlyModel.forEach((period) => {
      const row: CustomEmberTableRow & { className?: string } = {
        [RATE_COLUMNS.HISTORY]: period,
        [RATE_COLUMNS.DATE]: period,
        [RATE_COLUMNS.MY_OTB]: period,
        [RATE_COLUMNS.MARKET_OTB]: period,
        [RATE_COLUMNS.MARKET_DEMAND]: period,
        [RATE_COLUMNS.SHORT_TERM_RENTAL_OTB]: period,
        [RATE_COLUMNS.SHORT_TERM_RENTAL_RATE]: period,
      };

      this.filteredHotelinfos.forEach((hotelinfo) => {
        row[hotelinfo.id!] = period;
      });

      // Add the className property to highlight weekend rows
      if (isWeekendDate(period.day.date)) {
        row.className = 'table-row--highlighted';
      }

      rows.push(row);
    });

    return rows;
  }

  get showHistory(): boolean {
    return (
      this.router.currentRoute?.queryParams['otaId'] !== OTA_IDS.ALL_CHANNELS
    );
  }

  get tableContainerHeight(): string {
    let height = this.args.containerHeight - this.args.reservedHeight;
    if (this.args.showOTBSelfServiceButton) {
      height -= OTB_SELF_SERVICE_BUTTON_WIDTH;
    } // 40 is the height of the OTB Self Service button

    return `${height}`;
  }

  <template>
    <div
      class="rates-table-container"
      style={{styleEntry "height" (concat this.tableContainerHeight "px")}}
    >
      <PrismTable
        @columns={{this.columns}}
        @containerWidthAdjustment={{-15}}
        @isLoading={{@isLoading}}
        @overlayMessage={{this.overlayMessage}}
        @rows={{this.rows}}
        @widthConstraint="gte-container-slack"
      />
    </div>
  </template>
}
